'use client';

import React from 'react';
import classnames from 'classnames';

import Iconography, { IconographyProps } from '../Iconography/Iconography';
import Typography from '../Typography/Typography';
import Row from '../Grid/Row/Row';
import Box from '../Box/Box';
import Skeleton from '../Skeleton/Skeleton';

import styles from './IconButton.module.scss';

const iconsWithStroke = ['close'];

type Theme = 'dark' | 'light';
type Color = 'brand' | 'neutral' | 'error-1';
type Size = 'large' | 'medium' | 'small';
type ButtonType = 'primary' | 'secondary' | 'tertiary';
type Variant = 'button' | 'link';
type IconPlacement = 'left' | 'right';
type Justify = 'start' | 'center' | 'end';
type Direction = 'ltr' | 'rtl';
type Shape = 'circle' | 'square';

interface BaseIconButtonProps {
  theme?: Theme;
  color?: Color;
  size?: Size;
  buttonType?: ButtonType;
  label?: string;
  variant?: Variant;
  icon?: Pick<IconographyProps, 'category' | 'name' | 'size'>;
  customIcon?: React.ReactNode;
  href?: string;
  target?: string;
  gaComponent: string;
  dataTestId: string;
  linkContext?: string;
  handleClick?: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  interactionType?: string;
  iconPlacement?: IconPlacement;
  justify?: Justify;
  className?: string;
  position?: string | number;
  direction?: Direction;
  download?: boolean;
  showSkeleton?: boolean;
  logEvents?: boolean;
  shape?: Shape;
}

export interface IconButtonProps extends BaseIconButtonProps {}

const IconButton: React.FC<IconButtonProps> = ({
  theme = 'dark',
  color = 'neutral',
  size = 'medium',
  buttonType = 'primary',
  label,
  variant = 'button',
  icon,
  customIcon,
  href,
  target,
  gaComponent,
  dataTestId,
  linkContext,
  handleClick,
  disabled = false,
  interactionType,
  iconPlacement = 'left',
  justify = 'start',
  className,
  position,
  direction = 'ltr',
  download = false,
  showSkeleton = false,
  logEvents = true,
  shape = 'circle',
}) => {
  const renderIcon = (iconData: NonNullable<IconButtonProps['icon']>) => {
    if (!iconData) return null;
    return (
      <Box
        className={classnames(
          styles[shape],
          styles[size],
          styles[`${theme}_${color}_${buttonType}`],
          {
            [styles.disabled]: disabled,
            [styles.strokeFill]: iconsWithStroke.includes(iconData.name),
          }
        )}
        mr={label && iconPlacement === 'left' ? 8 : 0}
        ml={label && iconPlacement === 'right' ? 8 : 0}
        direction={direction}
      >
        <Iconography className={styles.icon} {...iconData} />
      </Box>
    );
  };

  const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
    if (typeof window !== `undefined` && logEvents) {
      const dataLayerMap: Record<string, unknown> = {
        event: 'interaction',
        interaction__type: interactionType || 'cta',
        interaction__component: gaComponent,
      };

      if (variant === 'link') {
        dataLayerMap.interaction__destination = href;
      }

      if (label) dataLayerMap.interaction__text = label;
      if (linkContext) dataLayerMap.interaction__linkContext = linkContext;
      if (position) dataLayerMap.position = position;

      (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
    }

    if (handleClick) {
      handleClick(e);
    }
  };

  const baseClassName = classnames(styles.button, styles[`icon_${iconPlacement}`], className, {
    [styles.disabled]: disabled,
  });

  const content = (
    <>
      {showSkeleton ? (
        <Box mr={iconPlacement === 'left' ? 8 : 0} ml={iconPlacement === 'right' ? 8 : 0}>
          <Skeleton className={styles[size]} width={{ xs: 40 }} borderRadius="50px" theme={theme} />
        </Box>
      ) : icon ? (
        renderIcon(icon)
      ) : (
        customIcon && (
          <Box
            className={classnames(
              styles[shape],
              styles[size],
              styles[`${theme}_${color}_${buttonType}`],
              {
                [styles.disabled]: disabled,
              }
            )}
            mr={label && iconPlacement === 'left' ? 8 : 0}
            ml={label && iconPlacement === 'right' ? 8 : 0}
            direction={direction}
          >
            {customIcon}
          </Box>
        )
      )}
      {label && (
        <Typography
          variant="button"
          size={size === 'small' ? size : 'medium'}
          color={disabled ? 'gray-5' : 'white'}
          theme={theme}
          showSkeleton={showSkeleton}
          direction={direction}
        >
          {label}
        </Typography>
      )}
    </>
  );

  return (
    <Row gutter={[0, 0]} align="middle" justify={justify} className={styles.iconButton}>
      {variant === 'link' ? (
        <a
          className={baseClassName}
          href={href}
          target={target}
          onClick={handleOnClick}
          data-testid={dataTestId}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          download={download}
        >
          {content}
        </a>
      ) : (
        <button
          className={baseClassName}
          onClick={handleOnClick}
          data-testid={dataTestId}
          disabled={disabled}
          type="button"
        >
          {content}
        </button>
      )}
    </Row>
  );
};

IconButton.displayName = 'IconButton';

export default IconButton;
